import React, { useState, useEffect, useRef } from "react";
import useCustomMutation from "../../../../Hooks/useCustomMutation";
import Post from "../../../../Hooks/post";
import { Row, Col } from "reactstrap";
import ButtonSubmit from "../../../../components/Buttons/ButtonSubmit";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";

function ParticipantProfile({ userId, userData, token }) {
  const key = ["MyUserDetails", userId];
  const mutation = useCustomMutation(key);
  const [inputs, setInputs] = useState({});
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (userData?.data?.data?._id) {
      const { firstName, lastName, email, phoneNumber, photo } = userData?.data?.data;
      setInputs({
        firstName: firstName || "",
        lastName: lastName || "",
        email: email || "",
        phoneNumber: phoneNumber || "",
      });
      if (photo) {
        setProfilePhoto(photo);
        setPhotoPreview(photo);
      }
    }
  }, [userData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size should be less than 5MB");
      return;
    }
    setProfilePhoto(file);
    const previewUrl = URL.createObjectURL(file);
    setPhotoPreview(previewUrl);
  };

  const uploadPhoto = async () => {
    if (!profilePhoto || !(profilePhoto instanceof File)) {
      return null;
    }

    setIsUploadingPhoto(true);
    
    try {
      const formData = new FormData();
      formData.append("photo", profilePhoto);
      formData.append("email", inputs.email || userData?.data?.data?.email);

      const result = await Post(
        "user/upload-photo",
        formData,
        "POST",
        "Photo uploaded",
        token,
        false
      );

      if (result?.status === "success") {
        return result?.data?.photo || result?.photo;
      }
      throw new Error(result?.message || "Photo upload failed");
      
    } catch (error) {
      console.error("Photo upload error:", error);
      throw error;
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // ✅ FIXED: Use FormData instead of JSON
  const updateProfileText = async () => {
    console.log("📤 Sending profile update as FormData...");
    
    const formData = new FormData();
    formData.append("firstName", inputs.firstName?.trim() || "");
    formData.append("lastName", inputs.lastName?.trim() || "");
    if (inputs.phoneNumber) {
      formData.append("phoneNumber", inputs.phoneNumber);
    }

    console.log("📦 FormData entries:");
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + pair[1]);
    }

    const result = await Post(
      "users/updateMe",
      formData,
      "PATCH",
      null,
      token,
      false  // ✅ isJSON: false for FormData
    );

    console.log("📥 Profile update result:", result);
    return result;
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    console.log("🚀 Form submitted");

    if (!inputs.firstName?.trim() || !inputs.lastName?.trim()) {
      alert("First name and last name are required");
      return;
    }

    try {
      let updatedPhotoUrl = null;
      if (profilePhoto instanceof File) {
        updatedPhotoUrl = await uploadPhoto();
      }

      await updateProfileText();

      if (userData?.data?.data?._id) {
        const updatedUserData = {
          ...userData,
          data: {
            ...userData.data,
            data: {
              ...userData.data.data,
              ...inputs,
              photo: updatedPhotoUrl || userData.data.data.photo,
            },
          },
        };
        localStorage.setItem("authEventKagoUser", JSON.stringify(updatedUserData));
      }

      alert("Profile updated successfully!");
      
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setProfilePhoto(updatedPhotoUrl || userData?.data?.data?.photo || null);
      
    } catch (error) {
      console.error("❌ Profile update error:", error);
      alert(`Failed to update profile: ${error.message || "Please try again"}`);
    }
  };

  const handlePhotoClick = () => fileInputRef.current?.click();

  const handleRemovePhoto = () => {
    setProfilePhoto(null);
    setPhotoPreview(userData?.data?.data?.photo || null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getPhotoFileName = () => {
    if (profilePhoto instanceof File) return profilePhoto.name;
    return null;
  };

  const isSubmitting = mutation?.isLoading || isUploadingPhoto;

  return (
    <div className="w-100 d-flex justify-content-center mt-3">
      <form className="form-create-event" onSubmit={handleSubmitForm}>
        <Row>
          <Col md={12} className="mb-4">
            <label className="event__input__form__Creation">Profile Photo</label>
            <div className="d-flex align-items-center gap-3">
              <div className="position-relative" style={{ width: "80px", height: "80px" }}>
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Profile preview"
                    className="rounded-circle"
                    style={{ 
                      width: "100%", 
                      height: "100%", 
                      objectFit: "cover",
                      border: "2px solid #f68a20"
                    }}
                  />
                ) : (
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ 
                      width: "100%", 
                      height: "100%", 
                      backgroundColor: "#f68a20",
                      color: "white",
                      fontSize: "24px",
                      fontWeight: "bold"
                    }}
                  >
                    {inputs.firstName?.[0] || "U"}{inputs.lastName?.[0] || "S"}
                  </div>
                )}
              </div>
              
              <div className="d-flex flex-column gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoSelect}
                  accept="image/*"
                  style={{ display: "none" }}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handlePhotoClick}
                  disabled={isSubmitting}
                >
                  {isUploadingPhoto ? "Uploading..." : "Choose Photo"}
                </button>
                {(profilePhoto instanceof File || photoPreview) && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleRemovePhoto}
                    disabled={isSubmitting}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
            
            {getPhotoFileName() && (
              <small className="text-muted d-block mt-1">
                Selected: {getPhotoFileName()}
              </small>
            )}
          </Col>

          <Col md={6}>
            <label className="event__input__form__Creation">First name </label>
            <input
              type="text"
              name="firstName"
              className="mb-3 form-control fromInput w-100"
              value={inputs?.firstName || ""}
              onChange={handleChange}
              required
              disabled={isSubmitting}
            />
          </Col>

          <Col md={6}>
            <label className="event__input__form__Creation">Last name</label>
            <input
              type="text"
              name="lastName"
              className="mb-3 form-control fromInput w-100"
              value={inputs?.lastName || ""}
              onChange={handleChange}
              required
              disabled={isSubmitting}
            />
          </Col>

          <Col md={6}>
            <label className="event__input__form__Creation">Email</label>
            <input
              type="email"
              name="email"
              className="mb-3 form-control fromInput w-100"
              value={inputs?.email || ""}
              onChange={handleChange}
              disabled={true}
            />
          </Col>

          <Col md={6}>
            <label>Phone Number</label>
            <div className="phone-input-container">
              <PhoneInput
                international
                countryCallingCodeEditable={false}
                defaultCountry="ZA"
                value={inputs.phoneNumber || null}
                onChange={(value) =>
                  setInputs((prev) => ({ ...prev, phoneNumber: value }))
                }
                className="custom-phone-input form-control"
                disabled={isSubmitting}
              />
            </div>
          </Col>

          <div className="d-flex justify-content-center align-items-center mt-4">
            <ButtonSubmit
              Title={isSubmitting ? "Updating..." : "Update Profile"}
              BackgroundColor="#f68a20"
              ColorText="white"
              BorderColor="#f68a20"
              borderRadius={20}
              handleOnclick={handleSubmitForm}
              pending={isSubmitting}
              disabled={isSubmitting}
            />
          </div>
        </Row>
      </form>
    </div>
  );
}

export default ParticipantProfile;